import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Search = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild's search is semantic and AI-powered. Instead of matching exact
            keywords, it understands the meaning behind your query and returns
            relevant results across Events, Presentations, and Guilds. Access search
            through the{' '}
            <Link href='/docs/getting-started/command-menu'>Command Palette</Link> by
            typing into the search input.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/getting-started/command-menu-open.png'
          alt='Command Palette with search input where users can type natural language queries to find Events, Presentations, and Guilds'
          caption={t`Access search through the Command Palette search input`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='semantic-search' title={t`Semantic Search`}>
          <Paragraph size='$5'>
            <Trans>
              Search on Guild goes beyond simple keyword matching. It understands
              natural language, so you can describe what you are looking for in your
              own words. For example:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='search' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Topic queries</Paragraph> —
                  "events about React", "machine learning talks", "design system
                  workshops". Guild understands the subject matter and finds relevant
                  content even if the exact words do not appear in the title.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='clock' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Temporal queries</Paragraph> —
                  "events next week", "events in March", "past events", "upcoming
                  meetups". Guild interprets time references and filters results to the
                  right date range.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='location' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Geographic queries</Paragraph> —
                  "events in London", "guilds near San Francisco", "meetups in Berlin".
                  Guild uses location data to find Events and Guilds in the area you
                  specify.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              You can combine these naturally — a query like "React events in London
              next month" uses topic, location, and time together to narrow your
              results.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='search-results' title={t`Search Results`}>
          <Paragraph size='$5'>
            <Trans>
              Results update in real-time as you type, so you can refine your query
              without waiting. Search results include three types of content:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Events</Paragraph> — Upcoming and
                  past gatherings that match your query. Results show the Event name,
                  date, location, and host Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Presentations</Paragraph> — Talks
                  and content that match your query. Results show the Presentation title,
                  speaker, and associated Event or Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Guilds</Paragraph> — Communities
                  that match your query. Results show the Guild name, description, and
                  Member count.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              Click any result to navigate directly to that Event, Presentation, or
              Guild page.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='search-tips' title={t`Tips for Better Results`}>
          <Paragraph size='$5'>
            <Trans>
              Be specific when you can — "TypeScript workshops in New York" will give
              more targeted results than just "TypeScript". If your first query does
              not surface what you need, try rephrasing it. The semantic engine often
              responds well to different phrasings of the same idea.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              If you already know the name of a Guild or Event, typing the exact name
              will bring it to the top of results quickly. For broader exploration,
              use descriptive queries to discover communities and events you might not
              have known about.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Search
